import { useEffect, useMemo, useState } from 'react';
import { chain, find, map, filter } from 'lodash';

import { Automation, AutomationType } from 'src/models/Automations/automation';
import threadsConfigurationResource from 'src/actions/threads/threadsConfigurationResource';
import threadsResource from 'src/actions/threads/threadsResource';
import useResource, {
  getConsolidatedState,
  someFailed,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
} from 'src/presentation/hooks/useResource';
import automationsResource from 'src/actions/automations/automationsResource';
import { useDispatch } from 'react-redux';
import { toastError } from 'src/actions/toast';
import Thread from 'src/models/Thread/Thread';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';

const useWismoAutomations = () => {
  const dispatch = useDispatch();

  const [options = [], setOptions] = useState<{ label: string; value: string }[]>([]);

  const { value: threadsConfiguration, loadingState: threadsConfigurationLoadingState } =
    useDocumentResource(threadsConfigurationResource);
  const { values: threads = [], loadingState: threadsLoadingState } = useResource(threadsResource, { fetch: false });
  const { values: automations = [], loadingState: automationsLoadingState } = useResource(automationsResource, {
    fetch: false,
  });

  const fetchError = someFailed(threadsLoadingState, automationsLoadingState, threadsConfigurationLoadingState);

  const loadingState = fetchError
    ? STATUS_FAILED
    : getConsolidatedState([threadsLoadingState, automationsLoadingState]);

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
      return;
    }
  }, [dispatch, fetchError]);

  const isThreadsFeatureActivated = useMemo(() => threadsConfiguration?.enabled ?? false, [threadsConfiguration]);

  useEffect(() => {
    if (isThreadsFeatureActivated) {
      // fetch threads and automations only if feature is activated...
      dispatch(threadsResource.effects.fetchAll());
      dispatch(automationsResource.effects.fetchAll());
    }
  }, [dispatch, isThreadsFeatureActivated, threadsConfiguration]);

  useEffect(() => {
    if (loadingState !== STATUS_SUCCEEDED) {
      return;
    }
    const threadsMap = chain<Thread>(threads).groupBy('id').value();
    const wismoAutomations = filter<Automation>(
      automations,
      automation => automation?.automationProvider?.type === AutomationType.AUTOMATION_VISUAL_IVR
    );
    const options = map(wismoAutomations, automation => ({
      value: automation.id,
      label: find(
        threadsMap[automation.parameters.ivrId],
        thread => thread.version === automation.parameters.ivrVersion
      )?.name,
    })) as { label: string; value: string }[];

    setOptions([{ label: 'No Thread assigned', value: '' }, ...options]);
  }, [automations, loadingState, threads, setOptions]);

  return { isThreadsFeatureActivated, options };
};

export default useWismoAutomations;
